import CookieNotice from '@/components/cookie-notice';
import { useSessionContext } from '@/contexts/session-context';
import { signOut } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import {
  FileEdit,
  Inbox,
  Loader2,
  LogOut,
  type LucideIcon,
  PenLine,
  Plane,
  Send,
  Shield,
  User,
  Users
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

interface NavItem {
  href: string;
  labelKey: string;
  icon: LucideIcon;
}

// Static nav items - labels resolved at render time
const NAV_ITEMS: NavItem[] = [
  { href: routes.inbox, labelKey: 'nav.inbox', icon: Inbox },
  { href: routes.sent, labelKey: 'nav.sent', icon: Send },
  { href: routes.drafts, labelKey: 'nav.drafts', icon: FileEdit },
  { href: routes.inTransit, labelKey: 'nav.inTransit', icon: Plane },
  { href: routes.discover, labelKey: 'nav.discover', icon: Users }
];

// Memoized nav link component
const NavLink = memo(function NavLink({
  href,
  labelKey,
  icon: Icon,
  isActive,
  variant
}: NavItem & { isActive: boolean; variant: 'desktop' | 'mobile' }) {
  const { t } = useTranslation();

  if (variant === 'mobile') {
    return (
      <Link
        to={href}
        className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all ${
          isActive ? 'text-ink' : 'text-sepia/60'
        }`}
      >
        <Icon className="w-5 h-5" />
        <span className="text-xs font-medium">{t(labelKey)}</span>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
        isActive ? 'bg-ink text-cream' : 'text-sepia hover:bg-gray-warm/30'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{t(labelKey)}</span>
    </Link>
  );
});

// Memoized sidebar navigation
const SidebarNav = memo(function SidebarNav({ pathname }: { pathname: string }) {
  return (
    <aside className="w-48 shrink-0 hidden md:block">
      <nav className="sticky top-24 space-y-1">
        {NAV_ITEMS.map((item) => (
          <NavLink key={item.href} {...item} isActive={pathname === item.href} variant="desktop" />
        ))}
      </nav>
    </aside>
  );
});

// Memoized mobile navigation
const MobileNav = memo(function MobileNav({ pathname }: { pathname: string }) {
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-warm/50 px-4 py-2 flex justify-around">
      {NAV_ITEMS.map((item) => (
        <NavLink key={item.href} {...item} isActive={pathname === item.href} variant="mobile" />
      ))}
    </nav>
  );
});

// Memoized header component
const Header = memo(function Header({
  isAdmin,
  onSignOut
}: {
  isAdmin: boolean;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();

  return (
    <header className="border-b border-gray-warm/50 bg-white/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link
          to={routes.inbox}
          className="flex items-center gap-2 text-2xl font-serif text-ink tracking-tight"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <radialGradient id="asg" cx="35%" cy="35%" r="65%">
                <stop offset="0%" stop-color="#c46b6b" />
                <stop offset="40%" stop-color="#A85959" />
                <stop offset="100%" stop-color="#8b3a3a" />
              </radialGradient>
            </defs>
            <circle cx="16" cy="16" r="15" fill="url(#asg)" />
            <circle
              cx="16"
              cy="16"
              r="12"
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              stroke-width="0.8"
            />
            <text
              x="16"
              y="21.5"
              textAnchor="middle"
              fontFamily="Georgia, serif"
              fontSize="17"
              fontWeight="700"
              fill="rgba(255,255,255,0.9)"
            >
              C
            </text>
          </svg>
          CANETA
        </Link>

        <div className="flex items-center gap-4">
          <Link
            to={routes.compose}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all"
            style={{ boxShadow: 'var(--shadow-sm)' }}
          >
            <PenLine className="w-4 h-4" />
            <span className="hidden sm:inline">{t('nav.writeLetter')}</span>
          </Link>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link
                to={routes.admin.dashboard}
                className="p-2 rounded-full hover:bg-gray-warm/30 transition-all"
                title={t('admin.title')}
              >
                <Shield className="w-5 h-5 text-sepia" />
              </Link>
            )}
            <Link
              to={routes.profile}
              className="p-2 rounded-full hover:bg-gray-warm/30 transition-all"
              title={t('nav.profile')}
            >
              <User className="w-5 h-5 text-sepia" />
            </Link>
            <button
              type="button"
              onClick={onSignOut}
              className="p-2 rounded-full hover:bg-gray-warm/30 transition-all"
              title={t('nav.signOut')}
            >
              <LogOut className="w-5 h-5 text-sepia" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
});

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isPending } = useSessionContext();

  useEffect(() => {
    if (!isPending && !user) {
      navigate(routes.login);
    }
  }, [user, isPending, navigate]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate(routes.login);
  }, [navigate]);

  const isAdmin = useMemo(() => user?.role === 'ADMIN', [user?.role]);

  if (isPending) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sepia animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream">
      <Header isAdmin={isAdmin} onSignOut={handleSignOut} />

      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        <SidebarNav pathname={location.pathname} />

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      <MobileNav pathname={location.pathname} />
      <CookieNotice />
    </div>
  );
}
